import React from 'react';
import { motion } from 'framer-motion';
import { Camera, CheckCircle2, ArrowRight, Info, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';
import { BeforeAfterCard } from '@/components/Cards';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';

const galleryItems = [
  {
    title: "Parkschaden Instandsetzung",
    beforeImage: IMAGES.BEFORE_AFTER_2,
    afterImage: IMAGES.BEFORE_AFTER_4,
    description: "Ein klassischer Parkschaden an der Seitentür. Durch präzise Hebeltechnik wurde die Delle ohne Lackierarbeiten vollständig entfernt."
  },
  {
    title: "Hagelschaden Reparatur",
    beforeImage: IMAGES.BEFORE_AFTER_5,
    afterImage: IMAGES.CAR_DETAIL_3,
    description: "Über 40 Hageleinschläge auf einer Aluminium-Motorhaube. Durch professionelle PDR-Technik wurden alle Dellen rückstandslos entfernt - ohne Lackierung."
  },
  {
    title: "Kotflügel Rempler",
    beforeImage: IMAGES.BEFORE_AFTER_4,
    afterImage: IMAGES.CAR_DETAIL_3,
    description: "Ein typischer Einkaufswagen-Rempler. In weniger als 60 Minuten war der Schaden rückstandslos behoben – effizient und kostengünstig."
  }
];

const Gallery: React.FC = () => {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-20 bg-muted/30 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Camera className="w-4 h-4" />
              <span>Referenzen & Ergebnisse</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Unsere Arbeit im <span className="text-primary">Vorher-Nachher</span> Vergleich
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Überzeugen Sie sich selbst von der Qualität unserer Paintless Dent Repair (PDR) Technik. 
              Wir erhalten den Originallack und den Wert Ihres Fahrzeugs.
            </p>
          </motion.div>
        </div>
        
        {/* Background Decor */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </section>

      {/* Gallery Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            {galleryItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <BeforeAfterCard
                  title={item.title}
                  beforeImage={item.beforeImage}
                  afterImage={item.afterImage}
                  description={item.description}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-20 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="flex flex-col gap-4 p-6 rounded-2xl bg-background border border-border/50 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">100% Lackschutz</h3>
              <p className="text-muted-foreground">
                Bei unserer Methode wird nicht geschliffen, gespachtelt oder nachlackiert. Der wertvolle Originallack bleibt zu 100% erhalten.
              </p>
            </div>

            <div className="flex flex-col gap-4 p-6 rounded-2xl bg-background border border-border/50 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Info className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Werterhalt</h3>
              <p className="text-muted-foreground">
                Keine Farbunterschiede und kein Wertverlust durch Unfallschaden-Vermerke. Ideal für Leasingrückläufer und Liebhaberfahrzeuge.
              </p>
            </div>

            <div className="flex flex-col gap-4 p-6 rounded-2xl bg-background border border-border/50 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Schnelle Abwicklung</h3>
              <p className="text-muted-foreground">
                Die meisten Dellen reparieren wir innerhalb weniger Stunden. Sie sparen Zeit und bis zu 70% Kosten gegenüber herkömmlichen Methoden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl bg-primary p-8 md:p-16 overflow-hidden">
            <div className="relative z-10 flex flex-col items-center text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
                Haben Sie eine ähnliche Delle an Ihrem Fahrzeug?
              </h2>
              <p className="text-primary-foreground/80 text-lg mb-10">
                Senden Sie uns einfach ein Foto per WhatsApp oder kommen Sie direkt vorbei. Wir erstellen Ihnen ein unverbindliches Angebot für die fachgerechte Reparatur.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Button asChild size="lg" variant="secondary" className="group">
                  <Link to={ROUTE_PATHS.CONTACT}>
                    Jetzt Kostenvoranschlag anfragen
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10">
                  <Link to={ROUTE_PATHS.SERVICES}>
                    Unsere Leistungen entdecken
                  </Link>
                </Button>
              </div>
            </div>
            
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--primary-foreground)_1px,_transparent_1px)] bg-[size:40px_40px]" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;